import { Store } from "../utils/Store";
import { getCartItemCount } from "./itemCount";
import type { Money, PriceConfiguration } from "./BaseTypes";
import type { IPriceConfigStore } from "./PriceConfigStore";
import type { ICartStore } from "./common-cart/CommonCartStore";
import { getConfiguredTotalPrice } from "./PriceFormatUtil";
import { GdprStorage } from "../uidispatcherapi/GdprStorage";
export type CartItemChange = {
	eventId: string;
	articleDescription: string;
	newQuantity: number;
};
export type MiniCartStoreState = {
	articleCount: number;
	isCartLoaded: boolean;
	priceConfiguration: PriceConfiguration;
	totalPrice: Money | null | undefined;
} & MiniCartStoreStateInternal;
type MiniCartStoreStateInternal = {
	cartItemChange: CartItemChange | null | undefined;
	cartRedirectPath: string;
	disabled: boolean;
	messageKeyAmountZero: string;
	messageKeyAmountPositive: string;
};
const POPOVER_DURATION = 6000;
export class MiniCartStoreImpl extends Store {
	_state: MiniCartStoreStateInternal;
	_timeoutId: any = null;
	_cartStore: ICartStore;
	_lastEventIdStorageKey: string;
	_priceConfigStore: IPriceConfigStore;

	constructor(
		cartStore: ICartStore,
		cartRedirectPath: string,
		lastEventIdStorageKey: string,
		disabled: boolean,
		messageKeyAmountZero: string,
		messageKeyAmountPositive: string,
		priceConfigStore: IPriceConfigStore
	) {
		super();
		this._state = {
			cartItemChange: null,
			cartRedirectPath,
			disabled,
			messageKeyAmountZero,
			messageKeyAmountPositive
		};
		this._cartStore = cartStore;
		this._lastEventIdStorageKey = lastEventIdStorageKey;
		this._priceConfigStore = priceConfigStore;
		this.listenTo(this._cartStore, this.handleCartUpdate);
		this.listenTo(this._priceConfigStore, () => this.trigger());
	}

	handleCartUpdate() {
		if (!this._cartStore.getCartItemChange) {
			return;
		}

		const cartItemChange = this._cartStore.getCartItemChange();

		const lastEventId = GdprStorage.localStorage.read(this._lastEventIdStorageKey);

		if (!cartItemChange || cartItemChange.eventId === lastEventId) {
			this.trigger();
			return;
		}

		GdprStorage.localStorage.put(this._lastEventIdStorageKey, cartItemChange.eventId, GdprStorage.NECESSARY);
		this._state.cartItemChange = cartItemChange;

		if (this._timeoutId) {
			clearTimeout(this._timeoutId);
		}

		this._timeoutId = setTimeout(() => {
			this._state.cartItemChange = null;
			this._timeoutId = null;
			this.trigger();
		}, POPOVER_DURATION);
		this.trigger();
	}

	getState(): MiniCartStoreState {
		return {
			articleCount: this.getArticleCount(),
			totalPrice: this.getTotalPrice(),
			isCartLoaded: this.isCartLoaded(),
			cartItemChange: this._state.cartItemChange,
			disabled: this._state.disabled,
			cartRedirectPath: this._state.cartRedirectPath,
			priceConfiguration: this._priceConfigStore.getConfig(),
			messageKeyAmountZero: this._state.messageKeyAmountZero,
			messageKeyAmountPositive: this._state.messageKeyAmountPositive
		};
	}

	isCartLoaded(): boolean {
		return !!this._cartStore.getCart();
	}

	getArticleCount(): number {
		const cart = this._cartStore.getCart();

		const items = Object.values(cart?.items ?? {});
		return getCartItemCount(items).articleCount;
	}

	getTotalPrice(): Money | null | undefined {
		const cart = this._cartStore.getCart();

		const totalPrices = cart?.totalPrices;
		const articleTotals = totalPrices?.basket?.articles.total;
		const totalTotals = totalPrices?.basket?.total;
		if (!articleTotals) return null;
		return getConfiguredTotalPrice(articleTotals, totalTotals, this._priceConfigStore.getConfig());
	}
}
